// src/components/orders/OrderWizard.js - VERSIONE PWA CON GESTIONE OFFLINE
import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, Save, Clock, AlertTriangle } from 'lucide-react';

// Hook personalizzati
import { useWizard } from '../../hooks/useWizard';
import { useOrderValidation } from '../../hooks/useOrderValidation';
import useOrderDraft from '../../hooks/useOrderDraft';
import { useOfflineStorage } from '../../hooks/useOfflineStorage';
import ApiService from '../../services/api';

// Componenti wizard
import WizardHeader from './wizard/WizardHeader';
import WizardProgressBar from './wizard/WizardProgressBar';
import CustomerStep from './wizard/CustomerStep';
import ProductStep from './wizard/ProductStep';
import PaymentStep from './wizard/PaymentStep';
import SummaryStep from './wizard/SummaryStep';
import SuccessStep from './wizard/SuccessStep';

const OrderWizard = () => {
  // Stati locali
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [offlineOrderId, setOfflineOrderId] = useState(null);
  const [showOfflineNotice, setShowOfflineNotice] = useState(false);
  const [wizardInitialized, setWizardInitialized] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [loadingDraftData, setLoadingDraftData] = useState(false);

  // Hook gestione offline
  const { 
    isOnline, 
    saveOfflineOrder, 
    pendingSyncCount,
    syncPendingOrders,
    getCachedCustomers,
    getCachedProducts 
  } = useOfflineStorage();

  // Configurazione step del wizard
  const wizardSteps = [
    { 
      id: 1, 
      title: 'Cliente', 
      icon: 'User', 
      color: 'blue',
      description: 'Seleziona o crea cliente'
    },
    { 
      id: 2, 
      title: 'Prodotti', 
      icon: 'Package', 
      color: 'green',
      description: 'Aggiungi prodotti all\'ordine'
    },
    { 
      id: 3, 
      title: 'Pagamento', 
      icon: 'CreditCard', 
      color: 'orange',
      description: 'Modalità di pagamento'
    },
    { 
      id: 4, 
      title: 'Riepilogo', 
      icon: 'ShoppingCart', 
      color: 'green',
      description: 'Conferma ordine'
    }
  ];

  // Hook gestione bozze ordine con auto-save
  const { 
    customer, 
    orderItems, 
    paymentMethod, 
    updateCustomer, 
    updateOrderItems, 
    updatePaymentMethod,
    clearDraft,
    lastSaved,
    enableAutoSave,
    disableAutoSave
  } = useOrderDraft();

  // Hook validazioni
  const validations = useOrderValidation();

  // Funzione per caricare i dati di un ordine esistente
  const loadDraftOrder = async (draftId) => {
    setLoadingDraftData(true);
    // BLOCCA IMMEDIATAMENTE tutti gli useEffect
    setIsEditingDraft(true);
    setDraftLoaded(true);
    
    try {
      const orderData = await ApiService.getOrderById(draftId);
      // Trasforma i dati dell'ordine per il wizard
      const transformedCustomer = {
        id: orderData.customerId,
        companyName: orderData.customer.companyName || '',
        legalAddress: orderData.customer.legalAddress || '',
        city: orderData.customer.city || '',
        zipCode: orderData.customer.zipCode || '',
        province: orderData.customer.province || '',
        vatNumber: orderData.customer.vatNumber || '',
        sdiCode: orderData.customer.sdiCode || '',
        email: orderData.customer.email || '',
        phone: orderData.customer.phone || '',
        isSelected: true, // IMPORTANTE: deve essere selezionato per l'editing
        isNewCustomer: false,
        destinations: orderData.customer.destinations || []
      };

      // Trasforma i dati degli items per il wizard
      const transformedItems = (orderData.items || []).map(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const productData = item.product || {};
        
        // Il prodotto è già strutturato correttamente dall'API
        const product = {
          ...productData,
          price: parseFloat(productData.price) || unitPrice
        };
        
        return {
          id: `item_${item.id || Date.now()}_${Math.random()}`,
          product: product,
          codice: product.code || '',
          descrizione: product.description || '',
          pesoGr: product.weightGr || 0,
          um: product.unitMeasure || 'pz',
          quantita: quantity,
          prezzo: unitPrice,
          iva: product.vatRate === 'FOUR' ? 4 : product.vatRate === 'TEN' ? 10 : 22,
          imponibile: quantity * unitPrice
        };
      });

      // Carica i dati direttamente nel wizard
      const wizardData = {
        customer: transformedCustomer,
        orderItems: transformedItems,
        paymentMethod: orderData.paymentMethod || 'contrassegno'
      };
      
      updateData(wizardData);
      
      // Imposta la destinazione selezionata se presente
      if (orderData.destinationId && transformedCustomer.destinations) {
        const selectedDestination = transformedCustomer.destinations.find(dest => dest.id === orderData.destinationId);
        if (selectedDestination) {
          const destinationIndex = transformedCustomer.destinations.indexOf(selectedDestination);
          wizardData.selectedDestination = { ...selectedDestination, index: destinationIndex };
          updateData(wizardData); // Aggiorna di nuovo con la destinazione
        }
      }

      setEditingDraftId(draftId);
      
      // Pulisci l'URL per rimuovere il parametro draft
      const url = new URL(window.location);
      url.searchParams.delete('draft');
      window.history.replaceState({}, '', url);
      
    } catch (error) {
      console.error('Errore caricamento bozza ordine:', error);
      alert('Errore nel caricamento della bozza ordine');
    } finally {
      setLoadingDraftData(false);
    }
  };

  // Controlla parametri URL al caricamento
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const draftId = urlParams.get('draft');
    
    
    if (draftId) {
      // BLOCCA IMMEDIATAMENTE per evitare conflitti
      setIsEditingDraft(true);
      loadDraftOrder(draftId);
    }
  }, []);

  // Hook gestione wizard - inizializza sempre con dati vuoti, poi carica da localStorage o API
  const {
    currentStep,
    validSteps,
    completedSteps,
    data,
    setCurrentStep,
    setValidSteps,
    setCompletedSteps,
    invalidateStepsAfter,
    validateStep,
    goToStep,
    nextStep,
    prevStep,
    updateData,
  } = useWizard(wizardSteps, {
    customer: {
      companyName: '',
      legalAddress: '',
      city: '',
      zipCode: '',
      vatNumber: '',
      sdiCode: '',
      email: '',
      isNewCustomer: false
    },
    orderItems: [],
    paymentMethod: 'contrassegno'
  });

  // Mostra/nasconde notifica offline quando cambia lo stato
  useEffect(() => {
    if (!isOnline && !showOfflineNotice) {
      setShowOfflineNotice(true);
      // Auto-hide dopo 5 secondi
      setTimeout(() => setShowOfflineNotice(false), 5000);
    }
  }, [isOnline, showOfflineNotice]);

  // Abilita auto-save sempre
  useEffect(() => {
    if (wizardInitialized) {
      if (!isOnline) {
        // Aumenta frequenza auto-save in modalità offline
        enableAutoSave(5000); // Ogni 5 secondi invece di 30
      } else {
        // Ripristina frequenza normale quando online
        enableAutoSave(30000); // Ogni 30 secondi
      }
    }

    return () => disableAutoSave();
  }, [isOnline, wizardInitialized, enableAutoSave, disableAutoSave]);

  // Inizializza il wizard dopo il primo render
  useEffect(() => {
    setWizardInitialized(true);
  }, []);

  // Carica dati bozza nel wizard SOLO se non stiamo editando una bozza esistente
  useEffect(() => {

    // Carica SOLO se:
    // 1. Wizard è inizializzato
    // 2. NON stiamo editando una bozza esistente
    // 3. NON stiamo resettando
    // 4. NON stiamo ancora caricando dati di bozza
    // 5. NON abbiamo già caricato la bozza
    if (wizardInitialized && !isEditingDraft && !isResetting && !loadingDraftData && !draftLoaded) {
      // Se la bozza ha dati significativi, caricali nel wizard
      if (customer?.companyName || orderItems.length > 0) {
  
        updateData({
          customer: customer,
          orderItems: orderItems,
          paymentMethod: paymentMethod
        });
        
        setDraftLoaded(true);
      }
    }
  }, [wizardInitialized, isEditingDraft, isResetting, loadingDraftData, draftLoaded, customer, orderItems, paymentMethod, updateData]);

  // Sincronizza wizard data verso draft cache quando i dati del wizard cambiano
  // MA SOLO se non stiamo editando una bozza esistente
  useEffect(() => {
    if (currentStep > 3) return;
    if (wizardInitialized && !isEditingDraft && !isResetting && !loadingDraftData) {
      // Sincronizza customer se è cambiato (confronto per riferimento)
      if (data.customer && data.customer !== customer && JSON.stringify(data.customer) !== JSON.stringify(customer)) {
        updateCustomer(data.customer);
      }
      
      if (data.orderItems && data.orderItems !== orderItems && JSON.stringify(data.orderItems) !== JSON.stringify(orderItems)) {
        console.log('Sincronizzazione articoli ordine verso bozza');
        updateOrderItems(data.orderItems);
      }
    }
  }, [wizardInitialized, isEditingDraft, isResetting, loadingDraftData, data.customer, data.orderItems, customer, orderItems, updateCustomer, updateOrderItems]);

  // Funzione per salvare ordine offline
  const handleOfflineOrderSave = async (orderData) => {
    try {
      const offlineOrder = saveOfflineOrder(orderData);
      setOfflineOrderId(offlineOrder.id);
      
      // Mostra notifica successo offline
      return {
        success: true,
        message: 'Ordine salvato offline',
        orderId: offlineOrder.id
      };
    } catch (error) {
      console.error('Errore salvataggio ordine offline:', error);
      return {
        success: false,
        message: 'Errore nel salvataggio offline'
      };
    }
  };

  // Props comuni condivisi tra tutti gli step con funzionalità PWA
  const commonStepProps = {
    data,
    updateData,
    validateStep,
    validations,
    nextStep,
    prevStep,
    goToStep,
    invalidateStepsAfter,
    currentStep,
    setCurrentStep,
    validSteps,
    completedSteps,
    loading,
    setLoading,
    // Props specifici PWA
    isOnline,
    getCachedCustomers,
    getCachedProducts,
    handleOfflineOrderSave,
    offlineOrderId,
    editingDraftId,
    // Props per gestione draft
    updateCustomer,
    updateOrderItems,
    updatePaymentMethod,
    clearDraft
  };

  // Funzione per renderizzare lo step corrente
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <CustomerStep {...commonStepProps} />;
      case 2:
        return <ProductStep {...commonStepProps} />;
      case 3:
        return <PaymentStep {...commonStepProps} />;
      case 4:
        return (
          <SummaryStep 
            {...commonStepProps} 
            onSuccess={() => setSuccess(true)} 
          />
        );
      default:
        return <CustomerStep {...commonStepProps} />;
    }
  };

  // Gestione reset wizard dopo successo
  const handleWizardReset = useCallback(() => {
    setIsResetting(true);
    
    // Usa la funzione clearDraft dal hook per pulire la cache
    clearDraft();
    
    // Poi resetta tutti gli stati
    setSuccess(false);
    setCurrentStep(1);
    setDraftLoaded(false);
    setOfflineOrderId(null);
    setEditingDraftId(null);
    setIsEditingDraft(false);
    
    // Reset stati validazione
    setValidSteps(new Set([1]));
    setCompletedSteps([]);
    
    // Reset dati wizard
    updateData({
      customer: {
        companyName: '',
        legalAddress: '',
        city: '',
        vatNumber: '',
        sdiCode: '',
        email: '',
        isNewCustomer: false
      },
      orderItems: [],
      paymentMethod: 'contrassegno'
    });
    
    // Riabilita gli useEffect dopo un delay
    setTimeout(() => {
      setIsResetting(false);
    }, 500);
  }, [setCurrentStep, setValidSteps, setCompletedSteps, updateData, clearDraft]);

  // Funzione per sincronizzazione manuale
  const handleManualSync = async () => {
    if (!isOnline) {
      alert('Impossibile sincronizzare: dispositivo offline');
      return;
    }

    try {
      setLoading(true);
      await syncPendingOrders();
      alert('Sincronizzazione completata con successo!');
    } catch (error) {
      console.error('Errore sincronizzazione:', error);
      alert('Errore durante la sincronizzazione');
    } finally {
      setLoading(false);
    }
  };

  // Render componente successo con info offline
  if (success) {
    return (
      <SuccessStep 
        onReset={handleWizardReset} 
        isOnline={isOnline}
        offlineOrderId={offlineOrderId}
        pendingSyncCount={pendingSyncCount}
        onSyncOrders={handleManualSync}
      />
    );
  }

  // Render loading per caricamento bozza
  if (loadingDraftData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Caricamento bozza ordine...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-x-hidden">
      {/* Notifiche PWA */}
      <PWANotifications 
        isOnline={isOnline}
        showOfflineNotice={showOfflineNotice}
        pendingSyncCount={pendingSyncCount}
        lastSaved={lastSaved}
        onSyncOrders={handleManualSync}
        onDismissOfflineNotice={() => setShowOfflineNotice(false)}
      />

      {/* Header con informazioni wizard e stato PWA */}
      <WizardHeader 
        steps={wizardSteps}
        currentStep={currentStep}
        validSteps={validSteps}
        completedSteps={completedSteps}
        onStepClick={(stepNumber) => goToStep(stepNumber, {
          1: validations.customer,
          2: validations.products,
          3: validations.payment
        })}
        isOnline={isOnline}
        lastSaved={lastSaved}
        pendingSyncCount={pendingSyncCount}
        onClearDraft={handleWizardReset}
      />
      
      {/* Barra di progresso */}
      <WizardProgressBar 
        steps={wizardSteps}
        currentStep={currentStep}
        validSteps={validSteps}
        completedSteps={completedSteps}
        onStepClick={(stepNumber) => goToStep(stepNumber, {
          1: validations.customer,
          2: validations.products,
          3: validations.payment
        })}
        isOnline={isOnline}
        onClearDraft={handleWizardReset}
        data={data}
        validations={validations}
      />

      {/* Contenuto principale */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            {renderCurrentStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente per le notifiche PWA
const PWANotifications = ({ 
  isOnline, 
  showOfflineNotice, 
  pendingSyncCount, 
  lastSaved,
  onSyncOrders,
  onDismissOfflineNotice 
}) => {
  return (
    <>
      {/* Notifica modalità offline */}
      {showOfflineNotice && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 max-w-sm mx-auto sm:mx-0 bg-orange-100 border border-orange-200 rounded-lg shadow-lg p-4">
          <div className="flex items-start space-x-3">
            <WifiOff className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-orange-900">Modalità Offline</h4>
              <p className="text-xs text-orange-800 mt-1">
                Puoi continuare a lavorare. Gli ordini verranno sincronizzati quando torni online.
              </p>
            </div>
            <button
              onClick={onDismissOfflineNotice}
              className="text-orange-400 hover:text-orange-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Banner ordini pendenti */}
      {isOnline && pendingSyncCount > 0 && (
        <div className="bg-blue-600 text-white px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">
                {pendingSyncCount} ordini da sincronizzare
              </span>
            </div>
            <button
              onClick={onSyncOrders}
              className="px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded text-sm font-medium transition-colors"
            >
              Sincronizza ora
            </button>
          </div>
        </div>
      )}

      {/* Indicatore auto-save */}
      {lastSaved && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-40 max-w-xs mx-auto sm:mx-0 bg-green-100 border border-green-200 rounded-lg px-3 py-2 shadow-sm">
          <div className="flex items-center space-x-2">
            <Save className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-800">
              Salvato {new Date(lastSaved).toLocaleTimeString('it-IT', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderWizard;

// Export dei componenti singoli per testing e riutilizzo
export {
  CustomerStep,
  ProductStep,
  PaymentStep,
  SummaryStep,
  SuccessStep,
  WizardHeader,
  WizardProgressBar,
  PWANotifications
};